import React, { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface Differential {
  title: string;
  description: string;
  details: string[];
  icon: React.ReactNode;
  gradient: string;
}

const AboutDifferentials: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ triggerOnce: true });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const differentials: Differential[] = [
    {
      title: "Resultados Naturais",
      description: "Nossa filosofia: realçar sua beleza, não transformá-la",
      details: [
        "Técnicas de harmonização sutil",
        "Respeito às proporções faciais",
        "Produtos premium de alta qualidade",
        "Antes e depois documentados"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      gradient: "from-pink-400 to-rose-500"
    },
    {
      title: "Equipe Qualificada",
      description: "Profissionais certificados e em constante atualização",
      details: [
        "Certificações nacionais",
        "Treinamentos mensais em técnicas",
        "Participação em congressos",
        "Expertise de mais de 5000 atendimentos"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      gradient: "from-emerald-400 to-teal-500"
    }
  ];

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className="py-20 md:py-32 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C4962F' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-800 mb-6">
            Nossos <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">Diferenciais</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            O que torna a BotoHarmony referência em tratamentos estéticos e harmonização facial
          </p>
        </div>

        {/* Differentials Grid */}
        <div
          ref={elementRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {differentials.map((diff, index) => (
            <div
              key={index}
              className="group relative"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className={`relative bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-100 hover:border-transparent transition-all duration-500 cursor-pointer overflow-hidden ${
                  expandedIndex === index ? 'shadow-2xl' : 'hover:shadow-2xl'
                }`}
                onClick={() => toggleExpand(index)}
              >
                {/* Background Gradient on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${diff.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${diff.gradient} text-white mb-6 shadow-lg transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                    {diff.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-serif font-bold text-gray-800 mb-3">
                    {diff.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {diff.description}
                  </p>

                  {/* Expand Indicator */}
                  <div className="flex items-center gap-2 text-sm font-semibold text-secondary mb-4">
                    <span>{expandedIndex === index ? 'Ver menos' : 'Saiba mais'}</span>
                    <svg
                      className={`w-5 h-5 transform transition-transform duration-300 ${
                        expandedIndex === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* Expandable Details */}
                  <div
                    className={`transition-all duration-500 overflow-hidden ${
                      expandedIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pt-4 border-t border-gray-200 space-y-3">
                      {diff.details.map((detail, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3"
                          style={{
                            transitionDelay: expandedIndex === index ? `${i * 50}ms` : '0ms',
                          }}
                        >
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br ${diff.gradient} flex items-center justify-center mt-0.5`}>
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="text-gray-700 flex-1">{detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Decorative Corner */}
                <div className={`absolute bottom-4 right-4 w-20 h-20 bg-gradient-to-br ${diff.gradient} opacity-5 rounded-full group-hover:scale-150 transition-transform duration-700`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Statement */}
        <div className="mt-20 text-center">
          <div className="inline-block relative max-w-4xl">
            <div className="absolute inset-0 bg-gradient-to-r from-secondary/10 to-primary/10 blur-3xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-10 shadow-xl border border-gray-100">
              <p className="text-2xl md:text-3xl font-serif text-gray-800 mb-4">
                Excelência não é um ato, mas um <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary font-bold">hábito</span>
              </p>
              <p className="text-gray-600 text-lg">
                E nosso compromisso diário é entregar o melhor para cada cliente
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutDifferentials;
